import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  signal,
  inject,
  DestroyRef,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  MessageSquareHeart,
  X,
  Send,
  Construction,
} from 'lucide-angular';
import { FeedbackApiService } from './feedback-api.service';

interface FeedbackModalConfig {
  title: string;
  description: string;
  placeholder: string;
  icon: typeof MessageSquareHeart;
  showInProgressBadge: boolean;
}

@Component({
  selector: 'app-feedback-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './feedback-modal.html',
  styleUrl: './feedback-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackModalComponent {
  private readonly feedbackApiService = inject(FeedbackApiService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isOpen$ = this.isOpen$$.asObservable();

  protected readonly feedbackText = signal<string>('');
  protected readonly isSubmitted = signal<boolean>(false);
  protected readonly isSubmitting = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);

  private readonly feedbackType$$ = new BehaviorSubject<string>('general');
  private readonly feedbackTypeSignal = toSignal(this.feedbackType$$, {
    initialValue: 'general',
  });
  private readonly entityId$$ = new BehaviorSubject<string | null>(null);
  private readonly entityType$$ = new BehaviorSubject<string>('lesson_plan');

  protected readonly modalConfig = computed<FeedbackModalConfig>(() => {
    const type = this.feedbackTypeSignal();

    switch (type) {
      case 'edit_request':
        return {
          title: 'Request Edit Feature',
          description:
            "🚧 This feature is coming soon! Tell us what you'd like to edit – lesson plan content, slides, structure, or something else?",
          placeholder:
            "What would you like to edit? For example:\n• Change slide content or order\n• Modify lesson objectives\n• Update activity descriptions\n• Adjust time allocations\n• Something else...",
          icon: Construction,
          showInProgressBadge: true,
        };
      case 'grading_feedback':
        return {
          title: 'Share Your Feedback',
          description:
            'Help us improve the grading experience! Share your thoughts on the grading results, suggestions, or report any issues.',
          placeholder:
            "Tell us what you think... What features would you love to see? Any issues with the grading results you've encountered?",
          icon: MessageSquareHeart,
          showInProgressBadge: false,
        };
      case 'leave_feedback':
      case 'share_feedback':
      default:
        return {
          title: 'Share Your Feedback',
          description:
            'Help us make JustTeach even better! Share your thoughts, suggestions, or report any issues.',
          placeholder:
            "Tell us what you think... What features would you love to see? Any issues you've encountered?",
          icon: MessageSquareHeart,
          showInProgressBadge: false,
        };
    }
  });

  @Input()
  set isOpen(value: boolean) {
    this.isOpen$$.next(value);
    if (!value) {
      // Reset state when modal closes
      this.feedbackText.set('');
      this.isSubmitted.set(false);
      this.isSubmitting.set(false);
      this.error.set(null);
    }
  }

  @Input()
  set feedbackType(value: string) {
    this.feedbackType$$.next(value);
  }

  @Input()
  set entityId(value: string | null) {
    this.entityId$$.next(value);
  }

  @Input()
  set entityType(value: string) {
    this.entityType$$.next(value);
  }

  // Legacy support for lessonPlanId
  @Input()
  set lessonPlanId(value: string | null) {
    this.entityId$$.next(value);
    this.entityType$$.next('lesson_plan');
  }

  @Output() readonly closed = new EventEmitter<void>();

  protected readonly icons = {
    MessageSquareHeart,
    X,
    Send,
    Construction,
  };

  protected close(): void {
    this.closed.emit();
  }

  protected onFeedbackInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.feedbackText.set(target.value);
  }

  protected submitFeedback(): void {
    const text = this.feedbackText().trim();
    const entityId = this.entityId$$.value;
    const entityType = this.entityType$$.value;

    if (!text) {
      return;
    }

    if (!entityId) {
      this.error.set('Unable to submit feedback. Please try again later.');
      return;
    }

    this.isSubmitting.set(true);
    this.error.set(null);

    this.feedbackApiService
      .submitFeedback({
        feedbackType: this.feedbackType$$.value,
        feedbackText: text,
        entities: [{ id: entityId, type: entityType }],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitted.set(true);
          this.isSubmitting.set(false);

          // Auto-close after showing success message
          setTimeout(() => {
            this.close();
          }, 2000);
        },
        error: (err) => {
          console.error('Failed to submit feedback:', err);
          this.error.set('Failed to submit feedback. Please try again.');
          this.isSubmitting.set(false);
        },
      });
  }
}
